import React, { useEffect } from 'react';
import {
  BodyTextMedium,
  Button,
  ComponentTextMedium,
  HeadingText40,
  SectionHeaderSkeleton,
} from '@modernhealth/vital-components';
import { useFetchSeriesWithSessionsQuery } from '@modernhealth/circles-graphql/v3';
import { captureException } from '@sentry/browser';
import { useHistory } from 'react-router-dom';

import { InternalLink } from 'src/shared/components/InternalLink';
import { sortTopicsByFirstSession } from 'src/shared/utils/sortTopicsByFirstSession';
import { isPublicTopic } from 'src/shared/utils/isPublicCircle';

import { CircleTopicCard } from './CircleTopicCard';
import { SessionCardRowSkeleton } from './SessionCardSkeletons';
import { UpcomingCirclesRow, UpcomingCirclesRowSubheader } from '../pages/UpcomingCircles';

const DisplayUpcomingBySeries: React.FC = () => {
  const { data, loading, error, refetch } = useFetchSeriesWithSessionsQuery();
  const history = useHistory();

  useEffect(() => {
    if (!loading && !error && !data?.circleSeriesCollection?.items) {
      captureException(
        new Error('Missing required data in the response to render upcoming circles page'),
      );
      history.replace('/circles');
    }
  }, [error, loading, data?.circleSeriesCollection?.items, history]);

  if (error) {
    captureException(error);
    return (
      <div>
        Failed to load upcoming circles. <Button onClick={() => refetch()}>Retry?</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <SectionHeaderSkeleton />
        <SessionCardRowSkeleton />

        <SectionHeaderSkeleton />
        <SessionCardRowSkeleton />

        <SectionHeaderSkeleton />
        <SessionCardRowSkeleton />
      </div>
    );
  }

  const series = data?.circleSeriesCollection.items;

  if (!series) {
    captureException(error);
    return (
      <div>
        Failed to load upcoming circles. <Button onClick={() => refetch()}>Retry?</Button>
      </div>
    );
  }
  return (
    <>
      {series?.flatMap(series => {
        if (!series) return [];
        const sortedTopics = sortTopicsByFirstSession(series.topics.edges, 3);

        if (sortedTopics.length === 0) return [];

        return (
          <div
            key={series.id}
            css={{
              marginTop: 54,
            }}
          >
            <h3 id={`series-label-${series.slug}`}>
              <HeadingText40 as="span">{series.name}</HeadingText40>
            </h3>
            <UpcomingCirclesRowSubheader
              css={{
                marginBottom: 24,
              }}
            >
              <BodyTextMedium numberOfLines={1} fontColor="gray65">
                {series.shortDescription}
              </BodyTextMedium>

              {series.slug ? (
                <InternalLink
                  aria-labelledby={`series-label-${series.slug}`}
                  aria-describedby={`series-description-${series.slug}`}
                  to={`/series/${series.slug}`}
                >
                  <ComponentTextMedium>View all</ComponentTextMedium>
                </InternalLink>
              ) : null}
            </UpcomingCirclesRowSubheader>

            <UpcomingCirclesRow>
              {sortedTopics.map(topic => {
                if (isPublicTopic(topic)) return <CircleTopicCard key={topic.id} topic={topic} />;
                return null;
              })}
            </UpcomingCirclesRow>
          </div>
        );
      })}
    </>
  );
};

export default DisplayUpcomingBySeries;
